/**
 * PersonaConsultDialog — J7-W3 task 3b + 3c (PRD §12.3.10) / Sheet Migration Wave 5
 *
 * Combined create dialog for both single-persona and council consultations.
 * The mode toggle (CardSelector with "Single | Council") gates whether the
 * persona picker shows a single-select or multi-select.
 *
 * Sheet Migration W5: converted from Modal to FormSheet with useFormSnapshot.
 * Create-only — no URL routing.
 *
 * Single-persona path:
 *   POST /api/v1/actors/:personaId/consult
 *
 * Council path (≥ 2 personas):
 *   POST /api/v1/actors/council
 *
 * Both endpoints already enforce that personas are knowledge_persona type
 * (server-side ValidationError). The picker filters to persona-type actors
 * up-front so the UX never offers an invalid pick.
 *
 * LEAN-02 — the server prepends the SoT context to the persona prompt;
 * this dialog only collects the operator-facing question + scenario.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { AlertCircle, MessagesSquare } from 'lucide-react';
import { t } from '@/lib/i18n';
import { FormSheet } from '@/components/ui/FormSheet';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { Alert } from '@/components/ui/Alert';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { CardSelector } from '@/components/ui/CardSelector';
import { MultiSelect } from '@/components/ui/MultiSelect/MultiSelect';
import {
  useRorosConsultPersona,
  useRorosConsultCouncil,
  useRorosActors,
} from '@/hooks/roros/actors';
import type {
  RorosActorDTO,
  RorosPersonaConsultationDTO,
} from '@/types/roros/actors';

/**
 * Radix Select rejects empty-string values, so the "no reference" option
 * uses the explicit sentinel `__none__`. The submit handler converts the
 * sentinel back to undefined before sending.
 */
const REF_NONE = '__none__';
const REFERENCE_TYPES = [
  { value: REF_NONE, label: 'None' },
  { value: 'decision_note', label: 'Decision Note' },
  { value: 'work_item', label: 'Work Item' },
  { value: 'outcome', label: 'Outcome' },
  { value: 'metric', label: 'Metric' },
  { value: 'routine', label: 'Routine' },
];

// ─── Snapshot type & diff ─────────────────────────────────────────────────────

type ConsultSnapshot = {
  mode: 'single' | 'council';
  personaIds: string[];
  context: string;
  question: string;
  refType: string;
  refId: string;
};

function diffConsultSnapshots(
  base: ConsultSnapshot,
  cur: ConsultSnapshot,
): Partial<Record<keyof ConsultSnapshot, boolean>> {
  return {
    mode: base.mode !== cur.mode,
    personaIds: JSON.stringify(base.personaIds) !== JSON.stringify(cur.personaIds),
    context: base.context !== cur.context,
    question: base.question !== cur.question,
    refType: base.refType !== cur.refType,
    refId: base.refId !== cur.refId,
  };
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (consultation: RorosPersonaConsultationDTO | null) => void;
  /** Optional default persona to pre-select. Used by the "Consult" CTA on
   *  a knowledge-persona actor card. */
  defaultPersonaId?: string;
}

export function PersonaConsultDialog({ isOpen, onClose, onCreated, defaultPersonaId }: Props) {
  const consultMutation = useRorosConsultPersona();
  const councilMutation = useRorosConsultCouncil();
  const actorsQuery = useRorosActors({ type: 'knowledge_persona' });

  const [error, setError] = useState<string | null>(null);

  // ─── Form state via useFormSnapshot ──────────────────────────────────────
  // depKey = defaultPersonaId so the snapshot re-seeds when the default changes.
  const { state, setField, baseline, current } = useFormSnapshot<ConsultSnapshot>({
    initialFactory: () => ({
      mode: 'single' as const,
      personaIds: defaultPersonaId ? [defaultPersonaId] : [],
      context: '',
      question: '',
      refType: REF_NONE,
      refId: '',
    }),
    isOpen,
    depKey: defaultPersonaId ?? null,
  });

  // Reset error on open.
  useEffect(() => {
    if (isOpen) setError(null);
  }, [isOpen, defaultPersonaId]);

  const refTypeIsSet = state.refType !== REF_NONE;

  const personaOptions = useMemo(() => {
    return ((actorsQuery.data as RorosActorDTO[] | undefined) ?? [])
      .filter((a) => a.actorType === 'knowledge_persona' && a.status === 'active')
      .map((a) => ({ value: a.id, label: a.name }));
  }, [actorsQuery.data]);

  const isPending = consultMutation.isPending || councilMutation.isPending;

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = () => {
    setError(null);

    if (state.personaIds.length === 0) {
      setError(t('Pick at least one knowledge persona.'));
      return;
    }
    if (state.mode === 'council' && state.personaIds.length < 2) {
      setError(t('A council requires at least 2 personas.'));
      return;
    }
    if (!state.context.trim()) {
      setError(t('Context is required.'));
      return;
    }
    if (!state.question.trim()) {
      setError(t('Question is required.'));
      return;
    }
    if (refTypeIsSet && !state.refId.trim()) {
      setError(t('Reference id is required when a reference type is selected.'));
      return;
    }

    const sharedPayload = {
      context: state.context.trim(),
      question: state.question.trim(),
      reference_entity_type: refTypeIsSet ? state.refType : undefined,
      reference_entity_id: refTypeIsSet ? state.refId.trim() : undefined,
    };

    if (state.mode === 'single') {
      consultMutation.mutate(
        { personaId: state.personaIds[0], payload: sharedPayload },
        {
          onSuccess: (data) => {
            onCreated?.(data as RorosPersonaConsultationDTO);
            onClose();
          },
          onError: (err: any) => setError(err?.message ?? t('Failed to consult persona')),
        },
      );
    } else {
      councilMutation.mutate(
        { ...sharedPayload, persona_ids: state.personaIds },
        {
          onSuccess: (data) => {
            onCreated?.(data as RorosPersonaConsultationDTO);
            onClose();
          },
          onError: (err: any) => setError(err?.message ?? t('Failed to consult council')),
        },
      );
    }
  };

  return (
    <FormSheet<ConsultSnapshot>
      isOpen={isOpen}
      onClose={onClose}
      title={t('New Consultation')}
      mode="create"
      baseline={baseline}
      current={current}
      diff={diffConsultSnapshots}
      onSubmit={handleSubmit}
      isSubmitting={isPending}
      submitLabel={state.mode === 'council' ? t('Run Council') : t('Send Consultation')}
      submittingLabel={t('Sending…')}
      submitTestId="consultation-submit-button"
      width="md"
    >
      <div className="space-y-5 pt-2" data-testid="consultation-form">
        {error && (
          <Alert variant="error" title={t('Cannot start consultation')}>
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden />
              <span data-testid="consultation-form-error">{error}</span>
            </div>
          </Alert>
        )}

        {/* Mode toggle: Single vs Council */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('Mode')}
          </label>
          <CardSelector
            mode="single"
            selected={state.mode}
            onChange={(v) => {
              const newMode = v as 'single' | 'council';
              setField('mode', newMode);
              // When switching to single, keep only the first picked persona.
              if (newMode === 'single' && state.personaIds.length > 1) {
                setField('personaIds', [state.personaIds[0]]);
              }
            }}
            items={[
              {
                id: 'single',
                title: t('Single persona'),
                description: t("Get one knowledge persona's perspective on the question."),
                icon: <MessagesSquare className="h-4 w-4" aria-hidden />,
              },
              {
                id: 'council',
                title: t('Council (multi-persona)'),
                description: t('Aggregate 2+ personas + a synthesis (max 5).'),
                icon: <MessagesSquare className="h-4 w-4" aria-hidden />,
              },
            ]}
            data-testid="consultation-mode-selector"
          />
        </div>

        {/* Personas */}
        <div data-testid="consultation-personas-section">
          {state.mode === 'single' ? (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('Knowledge persona')} <span className="text-error-500" aria-hidden>*</span>
              </label>
              <Select
                value={state.personaIds[0] || undefined}
                placeholder={t('Select a persona…')}
                onValueChange={(v) => setField('personaIds', v ? [v] : [])}
                options={personaOptions}
                data-testid="consultation-persona-select"
              />
            </div>
          ) : (
            <MultiSelect
              label={t('Council personas (pick 2–5)')}
              value={state.personaIds}
              onChange={(v) => setField('personaIds', v.slice(0, 5))}
              options={personaOptions}
              placeholder={t('Pick personas…')}
            />
          )}
        </div>

        {/* Context */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('Context')} <span className="text-error-500" aria-hidden>*</span>
          </label>
          <Textarea
            rows={4}
            value={state.context}
            onChange={(e) => setField('context', e.target.value)}
            placeholder={t('What is the situation? Include the relevant facts the persona needs to know.')}
            data-testid="consultation-context-input"
          />
        </div>

        {/* Question */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('Question')} <span className="text-error-500" aria-hidden>*</span>
          </label>
          <Textarea
            rows={3}
            value={state.question}
            onChange={(e) => setField('question', e.target.value)}
            placeholder={t('What should the persona evaluate or recommend?')}
            data-testid="consultation-question-input"
          />
        </div>

        {/* Optional reference entity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('Reference type (optional)')}
            </label>
            <Select
              value={state.refType}
              onValueChange={(v) => setField('refType', v)}
              options={REFERENCE_TYPES.map((r) => ({ value: r.value, label: t(r.label) }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('Reference id')}
            </label>
            <Input
              type="text"
              value={state.refId}
              onChange={(e) => setField('refId', e.target.value)}
              disabled={!refTypeIsSet}
              placeholder={t('UUID')}
            />
          </div>
        </div>
      </div>
    </FormSheet>
  );
}
